import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ApiUrl from "./../Environment/ApiUrl";
import { useToast } from "../Toast/ToastContext";
import "../../assets/css/Loader.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

function ClosedLoans() {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [closedLoans, setClosedLoans] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showAllItems, setShowAllItems] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const itemLabel = (loanListItem) => {
    if (!loanListItem) return "-";
    const eng = loanListItem.product_name || "";
    const tam = loanListItem.item_name_tamil || "";
    return tam ? `${eng} / ${tam}` : eng || "-";
  };

  const fetchClosedLoans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${ApiUrl}/Api/loan.php`);
      let data = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
      const closed = data.filter((item) => item.loan_status === "Close");
      setClosedLoans(closed);
    } catch (error) {
      console.error("Error fetching closed loans:", error.message);
      toast.error("Failed to fetch closed loans", 3000, "topRight");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/loan");
  };

  const handlePrint = () => {
    try {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>Closed Loans Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; font-weight: bold; }
              .header { text-align: center; margin-bottom: 20px; }
            </style>
          </head>
          <body>
            <div class="header"><h2>Closed Loans Report</h2></div>
            <p>Generated on: ${new Date().toLocaleString()}</p>
            <table>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Loan ID</th>
                  <th>Customer ID</th>
                  <th>Customer Name</th>
                  <th>Loan Date</th>
                  <th>Category</th>
                  <th>Product</th>
                  <th>Loan Amount</th>
                  <th>Close Date</th>
                </tr>
              </thead>
              <tbody>
                ${filteredData.map((item, index) => `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${item.loan_id || ""}</td>
                    <td>${item.customer_id || ""}</td>
                    <td>${item.customer?.name || "-"}</td>
                    <td>${formatDate(item.loan_date) || ""}</td>
                    <td>${item.loan_list?.[0]?.category || "-"}</td>
                    <td>${itemLabel(item.loan_list?.[0])}</td>
                    <td>${item.loan_amount || ""}</td>
                    <td>${formatDate(item.loan_close_date) || ""}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
      toast.success("Print dialog opened!", 3000, "topRight");
    } catch (error) {
      console.error("Error printing:", error);
      toast.error("Error printing. Please try again", 3000, "topRight");
    }
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF('landscape');
      doc.setFontSize(18);
      doc.text("Closed Loans Report", 14, 22);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 35);

      const tableColumns = [
        "S.No", "Loan ID", "Customer ID", "Customer Name", "Loan Date",
        "Category", "Product", "Loan Amount", "Close Date"
      ];

      const tableRows = filteredData.map((item, index) => [
        index + 1,
        item.loan_id || "",
        item.customer_id || "",
        item.customer?.name || "-",
        formatDate(item.loan_date) || "",
        item.loan_list?.[0]?.category || "-",
        itemLabel(item.loan_list?.[0]),
        item.loan_amount || "",
        formatDate(item.loan_close_date) || "",
      ]);

      autoTable(doc, {
        head: [tableColumns],
        body: tableRows,
        startY: 45,
        theme: "striped",
        headStyles: {
          fillColor: [71, 71, 71],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: "bold",
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 2,
        },
        margin: { top: 45, right: 14, bottom: 20, left: 14 },
      });

      doc.save(`Closed_Loans_${new Date().toISOString().split("T")[0]}.pdf`);
      toast.success("PDF exported successfully!", 3000, "topRight");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Error exporting PDF. Please try again.", 3000, "topRight");
    }
  };

  const exportExcel = () => {
    try {
      const excelData = filteredData.map((item, index) => ({
        "S.No": index + 1,
        "Loan ID": item.loan_id || "",
        "Customer ID": item.customer_id || "",
        "Customer Name": item.customer?.name || "-",
        "Loan Date": formatDate(item.loan_date) || "",
        "Category": item.loan_list?.[0]?.category || "-",
        "Product": itemLabel(item.loan_list?.[0]),
        "Loan Amount": item.loan_amount || "",
        "Close Date": formatDate(item.loan_close_date) || "",
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wscols = [
        { wch: 8 }, { wch: 15 }, { wch: 15 }, { wch: 20 },
        { wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 12 }, { wch: 12 }
      ];
      ws["!cols"] = wscols;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Closed Loans");
      XLSX.writeFile(wb, `Closed_Loans_${new Date().toISOString().split("T")[0]}.xlsx`);
      toast.success("Excel exported successfully!", 3000, "topRight");
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Error exporting Excel. Please try again.", 3000, "topRight");
    }
  };

  const filteredData = closedLoans.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.loan_id?.toLowerCase().includes(searchLower) ||
      item.customer_id?.toLowerCase().includes(searchLower) ||
      item.customer?.name?.toLowerCase().includes(searchLower) ||
      item.loan_list?.[0]?.category?.toLowerCase().includes(searchLower) ||
      item.loan_list?.[0]?.product_name?.toLowerCase().includes(searchLower) ||
      item.loan_amount?.toString().includes(searchLower)
    );
  });

  const totalItems = filteredData.length;
  const totalPages = showAllItems ? 1 : Math.ceil(totalItems / itemsPerPage);
  const currentItems = showAllItems
    ? filteredData
    : filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === "All") {
      setShowAllItems(true);
      setCurrentPage(1);
    } else {
      setShowAllItems(false);
      setItemsPerPage(Number(value));
      setCurrentPage(1);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </button>
        </li>
      );
    }
    return pages;
  };

  useEffect(() => {
    fetchClosedLoans();
  }, []);

  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-message">
            <div className="loadinggif"></div>
          </div>
        </div>
      )}

      <div className="container-fluid py-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <button className="btn btn-secondary btn-sm" onClick={handleBack}>
            <i className="fa-solid fa-arrow-left me-2"></i> Back to Loans
          </button>
          <h5 className="text-danger m-0">Closed Loans List</h5>
          <div className="d-flex gap-2">
            <button className="btn btn-sm btn-info text-white" onClick={handlePrint}>
              <i className="fa-solid fa-print me-1"></i> Print
            </button>
            <button className="btn btn-sm btn-danger text-white" onClick={exportPDF}>
              <i className="fa-solid fa-file-pdf me-1"></i> PDF
            </button>
            <button className="btn btn-sm btn-success text-white" onClick={exportExcel}>
              <i className="fa-solid fa-file-excel me-1"></i> Excel
            </button>
          </div>
        </div>

        <div className="card shadow">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center">
                <label className="form-label text-dark mb-0 me-2">Show :</label>
                <select
                  className="form-control form-control-sm"
                  style={{ width: "80px" }}
                  onChange={handleItemsPerPageChange}
                  value={showAllItems ? "All" : itemsPerPage}
                >
                  <option value="All">All</option>
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>

              <div className="search-container">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                  <button className="btn btn-outline-secondary btn-sm" type="button">
                    <i className="fa-solid fa-search"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered table-striped">
                <thead className="bg-light">
                  <tr>
                    <th>S.No</th>
                    <th>Loan ID</th>
                    <th>Customer ID</th>
                    <th>Customer Name</th>
                    <th>Loan Date</th>
                    <th>Category</th>
                    <th>Product</th>
                    <th>Loan Amount (₹)</th>
                    <th className="text-danger">Close Date</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((item, index) => (
                      <tr key={item.id || index}>
                        <td>
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </td>
                        <td>{item.loan_id || "-"}</td>
                        <td>{item.customer_id || "-"}</td>
                        <td>{item.customer?.name || "-"}</td>
                        <td>{formatDate(item.loan_date)}</td>
                        <td>{item.loan_list?.[0]?.category || "-"}</td>
                        <td>
                          {item.loan_list?.[0]?.item_name_tamil
                            ? `${item.loan_list[0].product_name} / ${item.loan_list[0].item_name_tamil}`
                            : (item.loan_list?.[0]?.product_name || "-")}
                        </td>
                        <td>{item.loan_amount || "0"}</td>
                        <td>{formatDate(item.loan_close_date)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-4">
                        <i className="fa-solid fa-circle-info me-2"></i>
                        No closed loans found
                      </td>
                    </tr>
                  )}
                </tbody>
                {currentItems.length > 0 && (
                  <tfoot className="table-secondary">
                    <tr>
                      <th colSpan="7" className="text-end">Total Records:</th>
                      <th colSpan="2">{totalItems}</th>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {!showAllItems && totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <span className="text-muted small">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                </span>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => handlePageChange(1)}>
                        First
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                        Previous
                      </button>
                    </li>
                    {renderPageNumbers()}
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                        Next
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => handlePageChange(totalPages)}>
                        Last
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ClosedLoans;